import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, GripVertical } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useConfirm } from "../hooks/useConfirm";
import { useGames } from "../context/GamesContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import ImageUploader from "../components/ImageUploader";

const inputCls = "bg-[#121212] border-axe-border rounded-xl text-white h-12 px-4";
const EMPTY = { key: "", label: "", short_code: "", tagline: "", hero_image: "", order: 0, is_active: true };

function slugifyKey(text) {
  return (text || "").toLowerCase().replace(/[^a-z0-9]+/g, "").slice(0, 30);
}

export default function GamesEditor() {
  const { games, refresh } = useGames();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirm, ConfirmModal] = useConfirm();

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        key: slugifyKey(form.key),
        label: form.label,
        short_code: form.short_code ? form.short_code.toUpperCase() : "",
        tagline: form.tagline || "",
        hero_image: form.hero_image || "",
        order: Number(form.order) || 0,
        is_active: !!form.is_active,
      };
      if (form._editId) await api.put(`/games/${form._editId}`, payload);
      else await api.post("/games", payload);
      toast.success("Saved");
      setForm(null);
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function remove(id) {
    if (!(await confirm("Delete this game? Existing players/tournaments tagged with it will still carry the tag but new forms won't offer it."))) return;
    try {
      await api.delete(`/games/${id}`);
      toast.success("Deleted");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  }

  const sorted = [...games].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div data-testid="games-editor">
      <ConfirmModal />
      <div className="flex justify-end mb-5">
        <button
          onClick={() => setForm({ ...EMPTY, order: (sorted.at(-1)?.order || 0) + 1 })}
          className="inline-flex items-center gap-2 rounded-full bg-axe-tangerine px-5 py-2 text-xs uppercase tracking-[0.22em] font-bold text-black hover:bg-axe-tangerine-hover"
          data-testid="games-new"
        >
          <Plus size={14} /> New Game
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {sorted.map((g) => (
          <div key={g.id} className="rounded-2xl border border-axe-border bg-axe-surface overflow-hidden" data-testid={`games-row-${g.id}`}>
            <div className="relative aspect-[16/9] bg-axe-bg overflow-hidden">
              {g.hero_image && <img src={g.hero_image} alt={g.label} className="h-full w-full object-cover" />}
              <div className="absolute inset-0 bg-gradient-to-t from-axe-surface via-axe-surface/40 to-transparent" />
              <div className="absolute top-3 left-3 flex gap-2">
                <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.25em] font-bold bg-axe-purple text-white">
                  {g.short_code || g.key.slice(0, 3).toUpperCase()}
                </span>
                <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.25em] font-bold bg-black/60 border border-white/10 text-white">
                  order {g.order}
                </span>
                {!g.is_active && (
                  <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.25em] font-bold bg-red-500/80 text-white">
                    hidden
                  </span>
                )}
              </div>
            </div>
            <div className="p-4">
              <div className="font-display font-bold text-lg">{g.label}</div>
              <div className="mt-1 text-xs text-neutral-400">key: <code className="text-axe-tangerine">{g.key}</code></div>
              {g.tagline && <div className="mt-2 text-xs text-neutral-500">{g.tagline}</div>}
              <div className="mt-4 flex gap-3">
                <button onClick={() => setForm({ ...g, _editId: g.id })} className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.22em] font-bold text-axe-tangerine hover:underline" data-testid={`games-edit-${g.id}`}>
                  <Pencil size={12} /> Edit
                </button>
                <button onClick={() => remove(g.id)} className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.22em] font-bold text-red-400 hover:underline" data-testid={`games-delete-${g.id}`}>
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
          </div>
        ))}
        {sorted.length === 0 && <div className="col-span-full text-neutral-500 border border-axe-border rounded-2xl p-8 text-center">No games yet.</div>}
      </div>

      {form && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-start md:items-center justify-center p-4 overflow-y-auto" data-testid="games-modal">
          <form onSubmit={save} className="relative w-full max-w-xl bg-axe-surface border border-axe-border rounded-2xl p-6 md:p-8 space-y-5 my-10">
            <button type="button" onClick={() => setForm(null)} className="absolute right-4 top-4 p-2 text-neutral-400 hover:text-white" aria-label="Close"><X size={18} /></button>
            <h3 className="font-display font-black text-2xl">{form._editId ? "Edit game" : "New game"}</h3>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Label</Label>
                <Input required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value, key: form._editId ? form.key : slugifyKey(e.target.value) })} className={`mt-2 ${inputCls}`} placeholder="Rocket League" data-testid="game-label" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Key (slug)</Label>
                <Input required value={form.key} onChange={(e) => setForm({ ...form, key: slugifyKey(e.target.value) })} className={`mt-2 ${inputCls}`} placeholder="rocketleague" disabled={!!form._editId} data-testid="game-key" />
                <div className="mt-1 text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                  {form._editId ? "Cannot change key after creation" : "Lowercase, letters & numbers only"}
                </div>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Short code</Label>
                <Input value={form.short_code} onChange={(e) => setForm({ ...form, short_code: e.target.value.toUpperCase().slice(0, 4) })} className={`mt-2 ${inputCls}`} placeholder="RL" data-testid="game-short" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Tagline</Label>
                <Input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} className={`mt-2 ${inputCls}`} placeholder="3v3 car soccer" data-testid="game-tagline" />
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Hero Image (16:9)</Label>
              <div className="mt-2"><ImageUploader value={form.hero_image} onChange={(url) => setForm({ ...form, hero_image: url })} aspect="aspect-video" testId="game-hero-uploader" /></div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 items-center">
              <div>
                <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Sort order</Label>
                <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className={`mt-2 ${inputCls} max-w-[140px]`} data-testid="game-order" />
              </div>
              <div className="flex items-center gap-3 pt-6">
                <Switch checked={!!form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="game-active" />
                <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Active (show on site)</Label>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setForm(null)} className="rounded-full border border-axe-border px-6 py-2.5 text-xs uppercase tracking-[0.22em] font-bold hover:bg-axe-bg">Cancel</button>
              <button type="submit" disabled={saving} className="rounded-full bg-axe-purple px-7 py-2.5 text-xs uppercase tracking-[0.22em] font-bold text-white hover:bg-axe-purple-hover disabled:opacity-50" data-testid="game-save">
                {saving ? "Saving…" : form._editId ? "Update" : "Create"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
